import React, { useState, useEffect } from 'react';
import './ReviewsSection.css';

const ReviewsSection = ({ lawyerId }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
    totalReviews: 0
  });
  const [ratingStats, setRatingStats] = useState({
    averageRating: 0,
    totalReviews: 0,
    ratingBreakdown: {
      5: 0,
      4: 0,
      3: 0,
      2: 0,
      1: 0
    }
  });

  useEffect(() => {
    if (lawyerId) {
      fetchReviews();
    }
  }, [lawyerId, currentPage]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `/api/client/fetchLawyerReviews?lawyerId=${lawyerId}&page=${currentPage}&limit=5`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch reviews');
      }
      
      const data = await response.json();
      setReviews(data.reviews);
      setPagination(data.pagination);
      
      // Use rating statistics from API (overall stats, not just current page)
      if (data.ratingStats) {
        setRatingStats(data.ratingStats);
      }
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };



  const renderStars = (rating) => {
    return (
      <div className="stars-display">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`star ${star <= rating ? 'filled' : ''}`}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getClientInitials = (client) => {
    if (!client) return 'A';
    return client.initials || 'A';
  };

  const getMaxRatingCount = () => {
    return Math.max(...Object.values(ratingStats.ratingBreakdown));
  };

  if (loading) {
    return (
      <div className="reviews-section">
        <div className="reviews-header">
          <h2>Client Reviews</h2>
        </div>
        <div className="loading-reviews">
          <div className="loading-spinner"></div>
          <p>Loading reviews...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reviews-section">
        <div className="reviews-header">
          <h2>Client Reviews</h2>
        </div>
        <div className="error-message">
          <p>{error}</p>
          <button onClick={fetchReviews} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="reviews-section">
      <div className="reviews-header">
        <div className="header-line"></div>
        <h2>Client Reviews</h2>
      </div>

      {reviews.length === 0 ? (
        <div className="no-reviews">
          <p>No reviews yet. Be the first to leave a review!</p>
        </div>
      ) : (
        <>
          {/* Ratings Summary Section */}
          <div className="ratings-summary">
            <div className="overall-ratings">
              <h3>Ratings</h3>
              <div className="average-rating">
                <span className="rating-number">{ratingStats.averageRating}</span>
                {renderStars(ratingStats.averageRating)}
              </div>
              <p className="ratings-subtitle">Ratings from real clients</p>
            </div>
            
            <div className="rating-breakdown">
              {[5, 4, 3, 2, 1].map((rating) => {
                const count = ratingStats.ratingBreakdown[rating];
                const maxCount = getMaxRatingCount();
                const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
                
                return (
                  <div key={rating} className="rating-bar-item">
                    <span className="star-label">{rating} star</span>
                    <div className="rating-bar-container">
                      <div 
                        className="rating-bar" 
                        style={{ 
                          width: `${percentage}%`,
                          backgroundColor: rating === 5 ? '#144A8C' : 
                                        rating === 4 ? '#0EA5E9' : 
                                        rating === 3 ? '#8B5CF6' : '#F97316'
                        }}
                      ></div>
                    </div>
                    <span className="rating-count">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Individual Reviews */}
          <div className="reviews-list">
            {reviews.map((review) => (
              <div key={review.id} className="review-item">
                <div className="review-header">
                  <div className="client-info">
                    <div className="client-avatar">
                      {getClientInitials(review.client)}
                    </div>
                    <div className="client-details">
                      <span className="client-name">
                        {review.client ? review.client.name : 'Anonymous'}
                      </span>
                      <span className="client-location">
                        {review.client?.city && review.client?.state 
                          ? `${review.client.city}, ${review.client.state}`
                          : 'Unknown Location'}
                      </span>
                    </div>
                  </div>
                  <div className="review-rating">
                    {renderStars(review.rating)}
                    <span className="rating-number">{review.rating}.0</span>
                    <span className="review-date">
                      {formatDate(review.createdAt)}
                    </span>
                  </div>
                </div>
                
                {review.reviewText && (
                  <div className="review-text">
                    <h4 className="review-title">{review.reviewText}</h4>
                  </div>
                )}

                {(review.consultationQuality || review.communicationQuality || 
                  review.professionalism || review.overallExperience) && (
                  <div className="detailed-ratings">
                    {review.consultationQuality && (
                      <div className="rating-item">
                        <span>Consultation Quality:</span>
                        {renderStars(review.consultationQuality)}
                      </div>
                    )}
                    {review.communicationQuality && (
                      <div className="rating-item">
                        <span>Communication:</span>
                        {renderStars(review.communicationQuality)}
                      </div>
                    )}
                    {review.professionalism && (
                      <div className="rating-item">
                        <span>Professionalism:</span>
                        {renderStars(review.professionalism)}
                      </div>
                    )}
                    {review.overallExperience && (
                      <div className="rating-item">
                        <span>Overall Experience:</span>
                        {renderStars(review.overallExperience)}
                      </div>
                    )}
                  </div>
                )}

                {review.wouldRecommend !== null && (
                  <div className="recommendation">
                    <span className={`recommend-badge ${review.wouldRecommend ? 'recommended' : 'not-recommended'}`}>
                      {review.wouldRecommend ? '✓ Would recommend' : '✗ Would not recommend'}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button className="see-more-btn">
                See more client reviews
                <span className="chevron">▼</span>
              </button>
              
              <div className="page-numbers">
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`page-number ${page === currentPage ? 'active' : ''}`}
                  >
                    {page}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReviewsSection; 